<template>
  <div
    class="fixed top-0 left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue"
  >
    <HeaderMini
      :name="$t('global.defaults.editTenancy')"
      :to="'/service-admin/tenancies/' + tenancy.tenantId"
      :no-menu="true"
    ></HeaderMini>
    <HeaderShadow></HeaderShadow>

    <div class="p-4">
      <form @submit.prevent="editTenancy">
        <div class="flex pb-4">
          <div class="mr-2 w-1/3">
            <router-link to="/service-admin/tenancies">
              <ButtonSecondary
                type="button"
                :name="$t('buttons.cancel')"
                :disabled="loading"
              ></ButtonSecondary>
            </router-link>
          </div>
          <div class="ml-2 w-2/3">
            <Button
              type="submit"
              :name="$t('buttons.save')"
              :disabled="
                !required(tenancy.name) ||
                !required(tenancy.contact.name) ||
                !validateEmail(tenancy.contact.email) ||
                !validatePhoneNumber(
                  tenancy.contact.countryCode,
                  tenancy.contact.phone
                ) ||
                loading
              "
              :loading="loading"
            ></Button>
          </div>
        </div>

        <div
          class="bg-nucleus-white transition-all"
          :class="{ 'opacity-50': loading }"
        >
          <div class="flex items-center px-4 py-6 border-b">
            <div
              class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-tenancy"
            ></div>
            <div class="text-2xl">
              {{ $t("global.defaults.editTenancy") }}
            </div>
          </div>
          <div class="px-4 py-6 overflow-y-auto scroll-box">
            <Input
              id="tenant-name"
              v-model:value="tenancy.name"
              input-type="text"
              :label="$t('inputs.tenancyName.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.tenancyName.placeholder')"
              required
            />
            <Select
              id="tenant-language"
              v-model:value="tenancy.language"
              :label="$t('inputs.language.label')"
              :options="languages"
              required
            />
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.licenseDetails") }}
              </div>
            </div>
            <Input
              id="license"
              v-model:value="tenancy.license"
              input-type="text"
              :label="$t('inputs.license.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.license.placeholder')"
              required
            />
            <Input
              id="license-expiry"
              v-model:value="tenancy.licenseExpiry"
              input-type="date"
              :label="$t('inputs.licenseExpiry.label')"
              autocomplete="off"
              required
            />
            <Input
              id="allowance-warning-threshold"
              v-model:value="tenancy.allowanceWarningThreshold"
              input-type="number"
              :label="$t('inputs.allowanceWarningThreshold.label')"
              :max-length="inputMaxLengths.allowanceWarningThreshold"
              :placeholder="$t('inputs.allowanceWarningThreshold.placeholder')"
            />
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.contactDetails") }}
              </div>
            </div>
            <Input
              id="contact-name"
              v-model:value="tenancy.contact.name"
              input-type="text"
              :label="$t('inputs.name.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.name.placeholder')"
              required
            />
            <Input
              id="contact-email"
              v-model:value="tenancy.contact.email"
              :update:value="validateEmail(tenancy.contact.email)"
              input-type="text"
              :label="$t('inputs.email.label')"
              class="relative"
              :max-length="inputMaxLengths.email"
              :placeholder="$t('inputs.email.placeholder')"
              :error="error.tenancy.contact.email"
              required
            />
            <div class="flex relative">
              <div class="mr-2 w-1/4">
                <SelectCountry
                  id="contact-phone-code"
                  v-model:value="tenancy.contact.countryCode"
                  :update:value="tenancy.contact.countryCode"
                  :label="$t('inputs.code.label')"
                  :options="countryCodes"
                  :options-common="countryCodesCommon"
                />
              </div>
              <div class="ml-2 w-3/4">
                <Input
                  id="contact-phone"
                  v-model:value="tenancy.contact.phone"
                  :update:value="
                    validatePhoneNumber(
                      tenancy.contact.countryCode,
                      tenancy.contact.phone
                    )
                  "
                  input-type="text"
                  :label="$t('inputs.phone.label')"
                  icon=""
                  :max-length="inputMaxLengths.phone"
                  :placeholder="$t('inputs.phone.placeholder')"
                  :error="error.tenancy.contact.phone"
                  required
                />
              </div>
            </div>
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.sessionSettings") }}
              </div>
            </div>
            <Input
              id="max-broadcasts"
              v-model:value="tenancy.maxBroadcasts"
              input-type="number"
              :label="$t('inputs.maxBroadcasts.label')"
              :max-length="inputMaxLengths.maxBroadcasts"
              :placeholder="$t('inputs.maxBroadcasts.placeholder')"
            />
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.apiDetails") }}
              </div>
            </div>
            <Select
              id="api-version"
              v-model:value="tenancy.api.version"
              :label="$t('inputs.apiVersion.label')"
              :options="apiVersionOptions"
              required
            />
            <Input
              id="api-username"
              v-model:value="tenancy.api.username"
              input-type="text"
              :label="$t('inputs.apiUsername.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.apiUsername.placeholder')"
              autocomplete="off"
              required
            />
            <Input
              id="api-password"
              v-model:value="tenancy.api.password"
              :input-type="passwordInputType"
              :label="$t('inputs.apiPassword.label')"
              :icon="passwordIcon"
              :max-length="inputMaxLengths.password"
              :placeholder="$t('inputs.apiPassword.placeholder')"
              autocomplete="new-password"
              @updateInputType="
                (passwordInputType = $event.type), (passwordIcon = $event.icon)
              "
            />
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.ipWhitelist") }}
              </div>
            </div>
            <div class="font-semibold">
              {{ $t("pages.tenancy.assignedIps") }}
            </div>
            <div v-for="(ip, index) in tenancy.ipWhitelist" :key="ip">
              <span
                class="text-sm leading-none mr-1 transition text-nucleus-error rounded-full opacity-50 cursor-pointer icon-trash"
                :title="$t('titles.delete')"
                @click="deleteIp(index)"
              >
              </span>
              {{ ip }}
            </div>
            <Input
              id="ip-address"
              v-model:value="ipAddress"
              :update:value="validateIpAddress(ipAddress)"
              input-type="text"
              :label="$t('inputs.ipAddress.label')"
              :max-length="inputMaxLengths.ip"
              :placeholder="$t('inputs.ipAddress.placeholder')"
              autocomplete="off"
              class="mt-2"
              :error="error.tenancy.ipAddress"
            />
          </div>
        </div>
      </form>

      <ErrorBox
        v-if="errorHeading || errorContent"
        :heading="errorHeading"
        :content="errorContent"
        :fixed="errorFixed"
        @closeError="
          errorHeading = '';
          errorContent = '';
        "
      ></ErrorBox>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderMini from "@/components/global/HeaderMini.vue"; // @ is an alias to /src
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import InfoItem from "@/components/utils/InfoItem.vue"; // @ is an alias to /src
import Input from "@/components/utils/Input.vue"; // @ is an alias to /src
import Select from "@/components/utils/Select.vue"; // @ is an alias to /src
import SelectCountry from "@/components/utils/SelectCountry.vue"; // @ is an alias to /src
import HintLink from "@/components/utils/HintLink.vue"; // @ is an alias to /src
import Button from "@/components/utils/Button.vue"; // @ is an alias to /src
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue"; // @ is an alias to /src

import CountryCodesJson from "@/store/data/countryCodes.json"; // @ is an alias to /src
import CountryCodesCommonJson from "@/store/data/countryCodesCommon.json"; // @ is an alias to /src
import ApiVersionOptionsJson from "@/store/data/apiVersionOptions.json";
import LanguagesJson from "@/store/data/languages.json";
import ErrorBox from "@/components/utils/ErrorBox.vue";

import parsePhoneNumber from "libphonenumber-js";

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    HeaderMini,
    HeaderShadow,
    InfoItem,
    Input,
    Select,
    SelectCountry,
    HintLink,
    Button,
    ButtonSecondary,
    ErrorBox,
  },
  data() {
    return {
      apiVersionOptions: ApiVersionOptionsJson,
      countryCodes: CountryCodesJson,
      countryCodesCommon: CountryCodesCommonJson,
      languages: LanguagesJson,
      ipAddress: "",
      error: {
        tenancy: {
          contact: {
            email: "",
            phone: "",
          },
          ipAddress: "",
        },
      },
      usage: null,
      passwordInputType: "password",
      passwordIcon: "show",
      loading: false,
      correctNumber: "",
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      inputMaxLengths: InputMaxLengths[0],
    };
  },
  computed: {
    tenancy() {
      let tenancy = this.$store.state.tenancy.tenancy;
      if (tenancy.licenseExpiry) {
        tenancy.licenseExpiry = tenancy.licenseExpiry.slice(0, 10);
      }
      return tenancy;
    },
  },
  mounted() {
    this.getCountryCallingCode();
  },
  methods: {
    getCountryCallingCode() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let phoneNumber: any;
      phoneNumber = parsePhoneNumber(this.tenancy.contact.phone);
      if (phoneNumber) {
        this.tenancy.contact.phone = phoneNumber.nationalNumber;
        this.tenancy.contact.countryCode = String(
          "+" + phoneNumber.countryCallingCode
        );
      }
    },
    editTenancy() {
      this.loading = true;

      const number = this.correctNumber
        ? this.correctNumber
        : this.tenancy.contact.phone;

      this.tenancy.contact.phone = number;

      if (this.ipAddress) {
        if (!this.tenancy.ipWhitelist) {
          this.tenancy.ipWhitelist = [];
        }
        this.tenancy.ipWhitelist.push(this.ipAddress);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let data: any = {
        name: this.tenancy.name,
        contact: this.tenancy.contact,
        ipWhitelist: this.tenancy.ipWhitelist,
        license: this.tenancy.license,
        licenseExpiry: new Date(this.tenancy.licenseExpiry).toISOString(),
        api: {
          username: this.tenancy.api.username,
          version: this.tenancy.api.version,
        },
        language: this.tenancy.language,
        maxBroadcasts: parseInt(this.tenancy.maxBroadcasts),
        allowanceWarningThreshold: parseInt(
          this.tenancy.allowanceWarningThreshold
        ),
      };

      if (this.tenancy.api.password) {
        data.api.password = this.tenancy.api.password;
      }

      this.$store
        .dispatch("editTenancy", {
          tenancyId: this.tenancy.tenantId,
          data: data,
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response.data) {
            console.log("complete", response);
            this.$store.dispatch("changeUpdate", "tenancy");
            this.$router.push({
              name: "tenancyDetail",
              params: { tenancyId: this.$route.params.tenancyId },
            });
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    deleteIp(index: number) {
      console.log(index);
      this.tenancy.ipWhitelist.splice(index, 1);
    },
    validateEmail(email: string) {
      const mailformat =
        // eslint-disable-next-line no-useless-escape
        /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

      if (this.required(email)) {
        if (!email.match(mailformat)) {
          this.error.tenancy.contact.email = this.$t(
            "inputs.email.errorInvalid"
          );
          return false;
        } else {
          this.error.tenancy.contact.email = "";
          return true;
        }
      } else {
        return false;
      }
    },
    validatePhoneNumber(code: any, phone: any) {
      const phoneNumber = parsePhoneNumber(
        code + phone.replace(/^0+/, "").replace(/\s/g, "")
      );

      if (this.required(phone)) {
        if (phoneNumber?.isValid()) {
          this.error.tenancy.contact.phone = "";
          this.correctNumber = phoneNumber.number;
          return true;
        } else {
          this.error.tenancy.contact.phone = this.$t(
            "inputs.phone.errorInvalid"
          );
          return false;
        }
      } else {
        return false;
      }
    },
    validateIpAddress(ipAddress: string) {
      const ipv4regex =
        // eslint-disable-next-line no-useless-escape
        /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;

      const ipv4cidrregex =
        // eslint-disable-next-line no-useless-escape
        /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/(3[0-2]|[1-2][0-9]|[0-9]))$/;

      if (
        !ipAddress.match(ipv4regex) &&
        !ipAddress.match(ipv4cidrregex) &&
        ipAddress.length > 0
      ) {
        this.error.tenancy.ipAddress = this.$t("inputs.ipAddress.errorInvalid");
        return false;
      } else {
        this.error.tenancy.ipAddress = "";
        return true;
      }
    },
    required(input: string, inputName: string) {
      if (input.length > 0) {
        if (inputName) this.error[inputName] = "";
        return true;
      } else {
        if (inputName)
          this.error[inputName] =
            inputName + " " + this.$t("error.validation.isRequired");
        return false;
      }
    },
  },
})
export default class TenancyAdd extends Vue {}
</script>

<style lang="scss" scoped>
.scroll-box {
  height: calc(100vh - 278px);
}
</style>
